import classNames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import Icon from '../Controls/Icon/Icon';
import Spinner from '../Controls/Spinner/Spinner';
import { EmptyList } from '../EmptyList/EmptyList';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { LOCALES } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { throttle } from 'src/utils/perfomance';
import './style.scss';

interface IScrollableListProps<T> {
  list: T[];
  current_page: number;
  last_page: number;
  pages: number[];
  loading?: boolean;
  refreshing?: boolean;
  renderItem: (item: T, index: number) => any;
  fetch: (...args: any[]) => void;
  clear?: (cancellable: boolean) => void;
  isHaveNextPages?: boolean;
  scrollToTop: boolean;
  scrollbar?: boolean;
  scrollbarSize: 'small' | 'normal';
  autoFetch: boolean;
  emptyListInFooter: boolean;
  direction?: 'horizontal' | 'vertical';
  lastPageText?: string;
  showEmptyListImage?: boolean;
  byOffset?: boolean;
  emptyListText?: string;
  className?: string;
  listClassName?: string;
  scrollbarClassName?: string;
  emptyListTextClassName?: string;
}

@observer
export default class ScrollableList<T> extends React.Component<IScrollableListProps<T>> {
  wrapper: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: IScrollableListProps<any>) {
    super(props);

    if (props.clear) props.clear(false);
  }

  static defaultProps = {
    scrollbarSize: 'small',
    emptyListInFooter: true,
  };

  state = {
    scrollToTop: false,
  };

  componentDidMount() {
    if (this.props.autoFetch) this.fetchData(this.props.byOffset ? 0 : 1);

    if (this.wrapper.current) {
      if (this.props.direction === 'horizontal') this.wrapper.current.addEventListener('wheel', this.onWindowHorizontalScroll);
      else this.wrapper.current.addEventListener('scroll', this.throttleScroll);
    }
  }

  componentDidUpdate(prevProps: IScrollableListProps<T>) {
    if (this.props.pages.length === 0 && this.props.pages.length !== prevProps.pages.length && this.wrapper?.current) {
      this.wrapper.current.scrollTo({ top: 0 });
    }
  }

  componentWillUnmount = () => {
    if (this.props.clear) this.props.clear(true);

    if (this.wrapper.current) {
      if (this.props.direction === 'horizontal') this.wrapper.current.removeEventListener('wheel', this.onWindowHorizontalScroll);
      else this.wrapper.current.removeEventListener('scroll', this.throttleScroll);
    }
  };

  fetchData = (offset?: number) => {
    this.props.fetch(offset);
  };

  onWindowScroll = () => {
    if (!this.wrapper?.current) return;

    const height = this.wrapper.current.offsetHeight;
    const scrollHeight = this.wrapper.current.scrollHeight;
    const scrollOffset = this.wrapper.current.scrollTop;
    const scrollBottomLeft = scrollHeight - (height + scrollOffset);
    const lastPage = this.props.pages[this.props.pages.length - 1];

    if (scrollOffset > height) {
      this.setState({ scrollToTop: true });
    } else {
      this.setState({ scrollToTop: false });
    }

    if (this.props.loading) return;

    const isHaveNextPages = lastPage < this.props.last_page || (this.props.byOffset && this.props.isHaveNextPages);

    if (isHaveNextPages && scrollBottomLeft < height / 2) {
      this.fetchData(this.props.byOffset ? undefined : lastPage + 1);
    }
  };
  throttleScroll = throttle(this.onWindowScroll, 50);

  onWindowHorizontalScroll = (event: WheelEvent) => {
    event.preventDefault();
    if (!this.wrapper?.current) return;
    this.wrapper.current.scrollLeft += event.deltaY;

    const width = this.wrapper.current.offsetWidth;
    const scrollWidth = this.wrapper.current.scrollWidth;
    const scrollOffset = this.wrapper.current.scrollLeft;
    const scrollBottomLeft = localeStore.language === LOCALES.AR ? scrollWidth - (width - scrollOffset) : scrollWidth - (width + scrollOffset);

    const firstPage = this.props.pages[0];
    const lastPage = this.props.pages[this.props.pages.length - 1];
    if (this.props.loading) return;
    if (lastPage < this.props.last_page && scrollBottomLeft < width / 2) {
      this.fetchData(this.props.byOffset ? undefined : lastPage + 1);
    }
    if (firstPage > 0 && this.props.pages.length && this.props.pages[0] !== 1 && scrollBottomLeft < width / 2 && !this.props.byOffset) {
      this.fetchData(firstPage - 1);
      this.wrapper.current.scrollTo({
        left: this.wrapper.current.scrollWidth - (width + scrollOffset) - scrollBottomLeft,
      });
    }
  };

  renderFooter = () => {
    if (this.props.direction === 'horizontal') return null;

    if (!this.props.loading && this.props.current_page >= this.props.last_page && this.props.list.length && this.props.emptyListInFooter) {
      if (!this.props.current_page) {
        return null;
      }

      return (
        <EmptyList
          text={this.props.emptyListText || 'no_more_orders'}
          textClassName={this.props.emptyListTextClassName || ''}
          showEmptyListImage={this.props.showEmptyListImage}
        />
      );
    }

    return null;
  };

  renderEmptyList = () => {
    const isCurrentPageEqualLastPage = this.props.byOffset
      ? this.props.current_page === this.props.last_page
      : this.props.current_page === this.props.last_page || (!this.props.current_page && !this.props.last_page);

    if (!this.props.loading && isCurrentPageEqualLastPage && !this.props.list.length) {
      return (
        <EmptyList
          text={this.props.emptyListText || 'no_matching_order'}
          textClassName={this.props.emptyListTextClassName || ''}
          showEmptyListImage={this.props.showEmptyListImage}
        />
      );
    }

    return null;
  };

  scrollToTop = () => {
    if (this.wrapper?.current) {
      this.wrapper.current.scrollTo({ top: 0, behavior: 'smooth' });
      this.props.fetch(0);
    }
  };

  get scrollListClassName(): string {
    return classNames('scroll-list hide-scrollbar', { 'scroll-list_horizontal': this.props.direction === 'horizontal' }, this.props.listClassName);
  }

  get scrollbarClassName(): string {
    const scrollBarSize: string = this.props.scrollbarSize === 'normal' ? 'scrollable-block__scroll_normal' : 'scrollable-block__scroll_small';
    return classNames('scroll-list-wrapper', { 'hide-scrollbar': !this.props.scrollbar }, scrollBarSize, this.props.scrollbarClassName);
  }

  public render() {
    return (
      <div className={classNames('d-flex flex-column flex-grow-1 pos-relative', this.props.className)}>
        <div ref={this.wrapper} className={this.scrollbarClassName}>
          <div className={this.scrollListClassName}>
            {this.props.list.map(this.props.renderItem)}
            {this.renderEmptyList()}
            {this.renderFooter()}
          </div>
        </div>

        {this.props.refreshing || this.props.loading ? (
          <div className="scroll-list-refreshing d-flex">
            <div className="d-flex flex-grow-1 justify-content-center m-auto">
              <Spinner />
            </div>
          </div>
        ) : null}

        {this.props.scrollToTop && this.state.scrollToTop ? (
          <div onClick={this.scrollToTop} className="scroll-to">
            <Icon name={Icons.ArrowTop} size={24} className="color-mainblue" />
          </div>
        ) : null}
      </div>
    );
  }
}
